/**
 * AuditLogPanel — UNIFIED activity log panel used across the platform.
 *
 * One visual everywhere (matches the Matters audit panel design):
 * header (icon + "Activity Log" + entity name + close), gray scroll area,
 * white timeline card with action-coloured circles, badges per action,
 * structured details/diffs rendering ("from X to Y" / "→ Y"), user name +
 * timestamp, Load more button.
 *
 * Routes the fetch to the right backend endpoint based on entityType:
 *   matter   → GET /matters/{uuid}/audit-log
 *   task     → GET /tasks/{uuid}/audit-log
 *   project  → GET /projects/{uuid}/audit-log
 *   review   → GET /reviews/{uuid}/audit-log
 *   * (else) → GET /audit-logs/entity/{Type}/{uuid}
 *
 * Resource-specific wrappers (MatterAuditLog, TaskAuditLog, ProjectAuditLog,
 * ReviewAuditLog) are thin shells around this component so existing call sites
 * keep working.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import apis from '../../utils/apis';
import { fetchEntityAuditLog } from '../../store/actions/AuditLogActions';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import { formatDateTime } from '../../utils/functions';
import {
  PiXLight,
  PiPlusCircleLight,
  PiPencilSimpleLight,
  PiTrashLight,
  PiLinkLight,
  PiLinkBreakLight,
  PiUsersThreeLight,
  PiClockCounterClockwiseLight,
  PiArchiveLight,
  PiCheckCircleLight,
  PiFileTextLight,
  PiPaperclipLight,
  PiChatCircleTextLight,
  PiArrowCounterClockwiseLight,
  PiShareNetworkLight,
} from 'react-icons/pi';

export interface AuditDiff {
  field: string;
  label: string;
  from?: any;
  to?: any;
}

export interface AuditEntry {
  id: number | string;
  action: string;
  entity_type?: string;
  user_first_name?: string | null;
  user_last_name?: string | null;
  user_name?: string | null;
  timestamp?: string | null;
  created_at?: string | null;
  notes?: string | null;
  details?: string | null;
  before_data?: Record<string, any> | null;
  after_data?: Record<string, any> | null;
  differences?: AuditDiff[] | Record<string, any> | null;
}

export interface AuditLogPanelProps {
  entityType: string;
  entityId: string;
  entityName?: string;
  onClose: () => void;
  /** Optional: clicking an entry triggers this. ReviewSessionDetail uses it
   *  to jump to a finding. Most consumers leave it undefined. */
  onEntryClick?: (entry: AuditEntry) => void;
}

// ─── Default action visual config ────────────────────────────────────────
const ACTION_CONFIG: Record<string, { icon: React.ReactElement; color: string; bg: string; label: string }> = {
  create: { icon: <PiPlusCircleLight />, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/30', label: 'Created' },
  created: { icon: <PiPlusCircleLight />, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/30', label: 'Created' },
  matter_create: { icon: <PiPlusCircleLight />, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/30', label: 'Created' },
  task_create: { icon: <PiPlusCircleLight />, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/30', label: 'Created' },
  update: { icon: <PiPencilSimpleLight />, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/30', label: 'Updated' },
  updated: { icon: <PiPencilSimpleLight />, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/30', label: 'Updated' },
  matter_update: { icon: <PiPencilSimpleLight />, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/30', label: 'Updated' },
  task_update: { icon: <PiPencilSimpleLight />, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/30', label: 'Updated' },
  stage_update: { icon: <PiPencilSimpleLight />, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/30', label: 'Stage changed' },
  status_update: { icon: <PiPencilSimpleLight />, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/30', label: 'Status changed' },
  delete: { icon: <PiTrashLight />, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/30', label: 'Deleted' },
  deleted: { icon: <PiTrashLight />, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/30', label: 'Deleted' },
  matter_delete: { icon: <PiTrashLight />, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/30', label: 'Deleted' },
  task_delete: { icon: <PiTrashLight />, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/30', label: 'Deleted' },
  archive: { icon: <PiArchiveLight />, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/30', label: 'Archived' },
  archived: { icon: <PiArchiveLight />, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/30', label: 'Archived' },
  task_archive: { icon: <PiArchiveLight />, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/30', label: 'Archived' },
  restore: { icon: <PiArrowCounterClockwiseLight />, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/30', label: 'Restored' },
  restored: { icon: <PiArrowCounterClockwiseLight />, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/30', label: 'Restored' },
  complete: { icon: <PiCheckCircleLight />, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/30', label: 'Completed' },
  completed: { icon: <PiCheckCircleLight />, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/30', label: 'Completed' },
  source_add: { icon: <PiLinkLight />, color: 'text-teal-600', bg: 'bg-teal-50 dark:bg-teal-900/30', label: 'Source added' },
  source_remove: { icon: <PiLinkBreakLight />, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/30', label: 'Source removed' },
  document_upload: { icon: <PiFileTextLight />, color: 'text-sky-600', bg: 'bg-sky-50 dark:bg-sky-900/30', label: 'Document uploaded' },
  attachment_add: { icon: <PiPaperclipLight />, color: 'text-sky-600', bg: 'bg-sky-50 dark:bg-sky-900/30', label: 'Attachment added' },
  attachment_remove: { icon: <PiPaperclipLight />, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/30', label: 'Attachment removed' },
  comment_add: { icon: <PiChatCircleTextLight />, color: 'text-slate-600', bg: 'bg-slate-50 dark:bg-slate-900/30', label: 'Comment added' },
  collaborator_add: { icon: <PiUsersThreeLight />, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/30', label: 'Access granted' },
  collaborator_remove: { icon: <PiUsersThreeLight />, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/30', label: 'Access removed' },
  collaborator_role_update: { icon: <PiUsersThreeLight />, color: 'text-sky-600', bg: 'bg-sky-50 dark:bg-sky-900/30', label: 'Access changed' },
  collaborator_access_changed: { icon: <PiUsersThreeLight />, color: 'text-sky-600', bg: 'bg-sky-50 dark:bg-sky-900/30', label: 'Access changed' },
  client_add: { icon: <PiUsersThreeLight />, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/30', label: 'Client added' },
  client_remove: { icon: <PiUsersThreeLight />, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/30', label: 'Client removed' },
  client_role_update: { icon: <PiUsersThreeLight />, color: 'text-sky-600', bg: 'bg-sky-50 dark:bg-sky-900/30', label: 'Client role changed' },
  matter_link: { icon: <PiLinkLight />, color: 'text-teal-600', bg: 'bg-teal-50 dark:bg-teal-900/30', label: 'Matter linked' },
  link: { icon: <PiLinkLight />, color: 'text-teal-600', bg: 'bg-teal-50 dark:bg-teal-900/30', label: 'Linked' },
  unlink: { icon: <PiLinkBreakLight />, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/30', label: 'Unlinked' },
  share: { icon: <PiShareNetworkLight />, color: 'text-cyan-600', bg: 'bg-cyan-50 dark:bg-cyan-900/30', label: 'Shared' },
  shared: { icon: <PiShareNetworkLight />, color: 'text-cyan-600', bg: 'bg-cyan-50 dark:bg-cyan-900/30', label: 'Shared' },
};

const ACTION_BADGE_COLOR: Record<string, any> = {
  create: 'green', created: 'green', matter_create: 'green', task_create: 'green',
  update: 'blue', updated: 'blue', matter_update: 'blue', task_update: 'blue',
  stage_update: 'blue', status_update: 'blue',
  delete: 'red', deleted: 'red', matter_delete: 'red', task_delete: 'red',
  archive: 'amber', archived: 'amber', task_archive: 'amber',
  restore: 'emerald', restored: 'emerald',
  complete: 'emerald', completed: 'emerald',
  source_add: 'teal', source_remove: 'amber',
  document_upload: 'sky',
  attachment_add: 'sky', attachment_remove: 'amber',
  comment_add: 'slate',
  collaborator_add: 'emerald', collaborator_remove: 'amber',
  collaborator_role_update: 'sky', collaborator_access_changed: 'sky',
  client_add: 'emerald', client_remove: 'amber', client_role_update: 'sky',
  matter_link: 'teal', link: 'teal', unlink: 'amber',
  share: 'cyan', shared: 'cyan',
};

const renderEntryDetails = (entry: AuditEntry, t: (s: string) => string): React.ReactNode => {
  // details may arrive as a string OR an object — normalize to string
  let detailsStr: string | null = null;
  if (typeof entry.details === 'string') {
    detailsStr = entry.details.trim() || null;
  } else if (entry.details && typeof entry.details === 'object') {
    try {
      detailsStr = JSON.stringify(entry.details);
    } catch {
      detailsStr = null;
    }
  }
  if (detailsStr) {
    return <span className="text-xs text-gray-600 dark:text-gray-400">{detailsStr}</span>;
  }
  // Check entry.differences first, then fall back to details_json.differences
  // (the unified /audit-logs/entity endpoint returns differences inside details_json).
  let diffs: AuditDiff[] | null = null;
  if (Array.isArray(entry.differences)) {
    diffs = entry.differences as AuditDiff[];
  } else if (entry.differences && typeof entry.differences === 'object') {
    const inner = (entry.differences as any).differences;
    if (Array.isArray(inner)) diffs = inner as AuditDiff[];
  }
  if (!diffs) {
    const dj = (entry as any).details_json;
    if (dj && typeof dj === 'object' && Array.isArray(dj.differences)) {
      diffs = dj.differences as AuditDiff[];
    }
  }
  if (diffs && diffs.length > 0) {
    return (
      <span className="text-xs text-gray-600 dark:text-gray-400">
        {diffs.map((d, i) => (
          <span key={i}>
            {i > 0 && ', '}
            <span className="font-medium">{d.label}</span>
            {d.from != null && d.to != null
              ? <> {t('from')} <span className="text-gray-700 dark:text-gray-300">{String(d.from)}</span> {t('to')} <span className="text-gray-700 dark:text-gray-300">{String(d.to)}</span></>
              : d.to != null
                ? <> → <span className="text-gray-700 dark:text-gray-300">{String(d.to)}</span></>
                : null}
          </span>
        ))}
      </span>
    );
  }
  if (entry.notes) {
    const notesStr = typeof entry.notes === 'string' ? entry.notes : JSON.stringify(entry.notes);
    return <span className="text-xs text-gray-600 dark:text-gray-400">{notesStr}</span>;
  }
  return null;
};

const normalizePayload = (raw: any): { entries: AuditEntry[]; hasNext: boolean } => {
  if (!raw) return { entries: [], hasNext: false };
  // Many shapes: {entries, pagination:{has_next}} | {data, total_pages, page} | {items, total_pages, page}
  const entries: AuditEntry[] =
    raw.entries || raw.data || raw.items || raw.audit_log || [];
  const pg = raw.pagination || {};
  let hasNext: boolean = false;
  if (typeof pg.has_next === 'boolean') {
    hasNext = pg.has_next;
  } else if (typeof raw.total_pages === 'number' && typeof raw.page === 'number') {
    hasNext = raw.page < raw.total_pages;
  } else if (typeof raw.total === 'number' && typeof raw.per_page === 'number' && typeof raw.page === 'number') {
    hasNext = raw.page * raw.per_page < raw.total;
  }
  return { entries, hasNext };
};

const AuditLogPanel: React.FC<AuditLogPanelProps> = ({
  entityType,
  entityId,
  entityName,
  onClose,
  onEntryClick,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const loadEntries = useCallback(async (pageNum: number = 1) => {
    setIsLoading(true);
    try {
      let raw: any;
      const lower = (entityType || '').toLowerCase();
      if (lower === 'matter') {
        const resp = await apis.get(`/matters/${entityId}/audit-log`, { params: { page: pageNum, limit: 50 } });
        raw = resp?.data;
      } else if (lower === 'task') {
        const resp = await apis.get(`/tasks/${entityId}/audit-log`, { params: { page: pageNum, limit: 50 } });
        raw = resp?.data;
      } else if (lower === 'review' || lower === 'review_session') {
        const resp = await apis.get(`/reviews/${entityId}/audit-log`, { params: { page: pageNum, page_size: 50 } });
        raw = resp?.data;
      } else {
        // Generic entity audit log endpoint expects PascalCase entity_type
        const pascal = entityType.charAt(0).toUpperCase() + entityType.slice(1);
        raw = await dispatch(fetchEntityAuditLog(pascal, entityId, pageNum, 50) as any);
      }
      const { entries: list, hasNext } = normalizePayload(raw);
      if (pageNum === 1) {
        setEntries(list);
      } else {
        setEntries((prev) => [...prev, ...list]);
      }
      setHasMore(hasNext);
      setPage(pageNum);
    } catch {
      if (pageNum === 1) setEntries([]);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, entityId, entityType]);

  useEffect(() => { loadEntries(1); }, [loadEntries]);

  return (
    <>
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <PiClockCounterClockwiseLight className="w-5 h-5 text-gray-500 flex-shrink-0" />
            <h3 className="text-sm text-gray-900 dark:text-white truncate">{t('Activity Log')}</h3>
          </div>
          {entityName && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{entityName}</p>
          )}
        </div>
        <Button variant="neutral" size="small" onClick={onClose}>
          <PiXLight className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar-main px-5 py-4 bg-[#F5F5F5] dark:bg-gray-900" style={{ backgroundColor: '#F5F5F5' }}>
        {isLoading && entries.length === 0 ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulseFade flex-shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-3/4 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade" />
                  <div className="h-2.5 w-1/2 bg-gray-200 dark:bg-gray-700 rounded animate-pulseFade" />
                </div>
              </div>
            ))}
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-400 dark:text-gray-500">
            <PiClockCounterClockwiseLight className="w-8 h-8 mb-2 opacity-40" />
            <p className="text-sm">{t('No activity recorded yet.')}</p>
          </div>
        ) : (
          <div className="relative rounded-lg p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            <div className="absolute left-8 top-4 bottom-4 w-px bg-gray-200 dark:bg-gray-700" />
            <div className="space-y-4">
              {entries.map((entry) => {
                const config = ACTION_CONFIG[entry.action] || ACTION_CONFIG['update'];
                const badgeColor = ACTION_BADGE_COLOR[entry.action] || 'slate';
                const userName =
                  (entry.user_name && entry.user_name.trim()) ||
                  `${entry.user_first_name || ''} ${entry.user_last_name || ''}`.trim();
                const ts = entry.timestamp || entry.created_at;
                const clickable = !!onEntryClick;
                return (
                  <div
                    key={entry.id}
                    onClick={clickable ? () => onEntryClick!(entry) : undefined}
                    className={`relative flex gap-3 pl-0 ${clickable ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 rounded-md -mx-2 px-2 py-1' : ''}`}
                  >
                    <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${config.bg}`}>
                      <span className={`w-4 h-4 ${config.color}`}>{config.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge color={badgeColor}>{config.label}</Badge>
                        {renderEntryDetails(entry, t)}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {userName && (
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{userName}</span>
                        )}
                        {ts && (
                          <span className="text-[11px] text-gray-400 dark:text-gray-500">{formatDateTime(ts)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {hasMore && (
              <div className="flex justify-center pt-4">
                <button
                  onClick={() => loadEntries(page + 1)}
                  disabled={isLoading}
                  className="px-4 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-full hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  {isLoading ? t('Loading...') : t('Load more')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AuditLogPanel;
